import React, { useState, useEffect, useMemo } from 'react';
import { PLATFORM_ACQUISITION_FEE_RATE } from '../../config/constants';
import {
  formatUsd,
  formatExactMoney,
  getDisplayCountry,
  estimateInvestmentTotalUsd,
  getSupportedMarketTax,
} from '../../data/countries';
import { getPropertyCardImage } from '../../utils/properties';
import apiClient from '../../utils/api';
import SaleInvestmentCostBreakdown from '../charts/SaleInvestmentCostBreakdown';
import { formatOwnedSharesMessage } from '../../utils/investments';

export const formatInvestTaxLabel = (label, rate) => {
  const base = String(label || "Tax").trim();
  if (!rate || rate <= 0) return base;
  if (/%/.test(base)) return base;
  const pct = rate === 0.081 ? "8.1" : String(Math.round(rate * 1000) / 10).replace(/\.0$/, "");
  return `${base} (${pct}%)`;
};

export const InvestReceiptLine = ({ title, hint, value, valueMuted }) => (
  <div className="invest-receipt-line">
    <div className="invest-receipt-line__label">
      <strong>{title}</strong>
      {hint ? <span className="invest-receipt-line__hint">{hint}</span> : null}
    </div>
    <span className={`invest-receipt-line__value${valueMuted ? " invest-receipt-line__value--muted" : ""}`}>{value}</span>
  </div>
);

const InvestModal = ({ property, balance, existingShares = 0, onClose, onConfirm, country }) => {
  const [shares, setShares] = useState(1);
  const [sharesDraft, setSharesDraft] = useState(null);
  const [quote, setQuote] = useState(null);
  const [tokenizationEnabled, setTokenizationEnabled] = useState(false);
  const [cryptoCheckoutReady, setCryptoCheckoutReady] = useState(false);
  const [algoSwapReady, setAlgoSwapReady] = useState(false);
  const [testnetAlgoUsdRate, setTestnetAlgoUsdRate] = useState(null);
  const [algoSettlementMode, setAlgoSettlementMode] = useState(null);
  const [algoWallet, setAlgoWallet] = useState(null);
  const [settlementRail, setSettlementRail] = useState("fiat");
  const [payAsset, setPayAsset] = useState("USDCa");
  const [paying, setPaying] = useState(false);
  const pricePerShare = property.price / property.shares;
  const available = property.shares - (property.sharesSold || 0);
  const propCountry = property.country || "US";
  const displayCountry = getDisplayCountry(propCountry);
  const heroImage = getPropertyCardImage(property);

  const maxAffordableShares = (() => {
    let max = 0;
    for (let s = 1; s <= available; s++) {
      const { totalUsd } = estimateInvestmentTotalUsd(s * pricePerShare, propCountry);
      if (totalUsd <= balance + 0.001) max = s;
      else break;
    }
    return max;
  })();
  const maxShares =
    settlementRail === "algorand" && cryptoCheckoutReady
      ? available
      : Math.min(available, maxAffordableShares);
  const canInvest = maxShares >= 1;
  const usesCryptoCheckout = settlementRail === "algorand" && cryptoCheckoutReady;
  const MIN_ALGO_FEE_BUFFER = 0.3;

  const clampShares = (n) => Math.max(1, Math.min(maxShares, Math.floor(Number(n)) || 1));

  const draftSharesValue = useMemo(() => {
    if (sharesDraft === null || sharesDraft === "") return shares;
    const n = parseInt(sharesDraft, 10);
    return Number.isNaN(n) ? shares : clampShares(n);
  }, [shares, sharesDraft, maxShares]);

  const ownershipPct = property.shares > 0 ? ((draftSharesValue / property.shares) * 100) : 0;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.getTokenizationConfig();
        if (!cancelled && res.data?.enabled) {
          setTokenizationEnabled(true);
          setCryptoCheckoutReady(res.data?.cryptoCheckout?.ready === true);
        }
        const checkout = await apiClient.getCryptoCheckoutConfig();
        if (!cancelled && checkout.data) {
          if (checkout.data.ready) setCryptoCheckoutReady(true);
          setAlgoSwapReady(checkout.data.algoSwapReady === true);
          setTestnetAlgoUsdRate(
            typeof checkout.data.testnetAlgoUsdRate === 'number'
              ? checkout.data.testnetAlgoUsdRate
              : null
          );
          setAlgoSettlementMode(checkout.data.algoSettlementMode || null);
        }
      } catch (_) {
        /* tokenization optional */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!paying) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [paying]);

  useEffect(() => {
    if (!usesCryptoCheckout) {
      setAlgoWallet(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.getMyAlgorandWallet();
        if (!cancelled) setAlgoWallet(res.data || null);
      } catch (_) {
        if (!cancelled) setAlgoWallet(null);
      }
    })();
    return () => { cancelled = true; };
  }, [usesCryptoCheckout]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.getInvestmentQuote(property.id, draftSharesValue);
        if (!cancelled && res.data) setQuote(res.data);
      } catch (_) {
        if (!cancelled) {
          const tax = getSupportedMarketTax(propCountry);
          setQuote({
            ...estimateInvestmentTotalUsd(draftSharesValue * pricePerShare, propCountry),
            sharePriceUsd: pricePerShare,
            vatLabel: tax.label,
            vatNote: tax.note,
            chargeCurrency: "USD"
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [property.id, draftSharesValue, pricePerShare, propCountry]);

  useEffect(() => {
    if (maxShares >= 1 && shares > maxShares) setShares(maxShares);
  }, [maxShares, shares]);

  useEffect(() => {
    if (sharesDraft === null || sharesDraft === "") return;
    const n = parseInt(sharesDraft, 10);
    if (!Number.isNaN(n) && n > maxShares) setSharesDraft(String(maxShares));
  }, [maxShares, sharesDraft]);

  const subtotalUsd = quote?.subtotalUsd ?? draftSharesValue * pricePerShare;
  const acquisitionFeeUsd = quote?.acquisitionFeeUsd ?? Math.round(subtotalUsd * PLATFORM_ACQUISITION_FEE_RATE * 100) / 100;
  const vatAmountUsd = quote?.vatAmountUsd ?? 0;
  const totalUsd = quote?.totalUsd ?? subtotalUsd + acquisitionFeeUsd + vatAmountUsd;
  const taxMeta = getSupportedMarketTax(propCountry);
  const vatLabel = formatInvestTaxLabel(quote?.vatLabel || taxMeta.label, quote?.vatRate ?? taxMeta.rate);
  const vatNote = quote?.vatNote || taxMeta.note;
  const balanceAfter = balance - totalUsd;
  const ownedSharesMessage = formatOwnedSharesMessage(existingShares);

  const cryptoFundsWarning = useMemo(() => {
    if (!usesCryptoCheckout) return null;
    if (!algoWallet?.address) {
      return "Your Algorand wallet is loading or not ready. Try again in a moment.";
    }
    const algoBal = Number(algoWallet.algoBalance || 0);
    const usdcBal = Number(algoWallet.usdcBalance || 0);
    if (algoBal < MIN_ALGO_FEE_BUFFER) {
      return `Need at least ${MIN_ALGO_FEE_BUFFER} ALGO for network fees. You have ${algoBal.toLocaleString(undefined, { maximumFractionDigits: 6 })} ALGO. Fund under Add funds → ALGO / USDC.`;
    }
    if (payAsset === "USDCa") {
      if (usdcBal + 0.001 < totalUsd) {
        return `Insufficient USDC. Need ${formatUsd(totalUsd)} USDC; your wallet has ${usdcBal.toFixed(2)} USDC. Send USDC to your deposit address first.`;
      }
      return null;
    }
    if (payAsset === "ALGO") {
      if (!algoSwapReady) {
        return "ALGO checkout requires a live swap pool on this network. Use USDC or try again later.";
      }
      const peg = testnetAlgoUsdRate && testnetAlgoUsdRate > 0 ? testnetAlgoUsdRate : 0.15;
      const roughAlgoNeed = totalUsd / peg;
      if (algoBal < roughAlgoNeed + MIN_ALGO_FEE_BUFFER) {
        return `Insufficient ALGO. Need ~${roughAlgoNeed.toFixed(2)} ALGO plus fees; you have ${algoBal.toLocaleString(undefined, { maximumFractionDigits: 6 })} ALGO.`;
      }
    }
    return null;
  }, [usesCryptoCheckout, algoWallet, payAsset, totalUsd, algoSwapReady, testnetAlgoUsdRate]);

  const cryptoCheckoutBlocked = Boolean(usesCryptoCheckout && cryptoFundsWarning);

  const commitSharesInput = (raw) => {
    const digits = String(raw ?? "").replace(/\D/g, "");
    const n = digits === "" ? 1 : parseInt(digits, 10);
    const clamped = clampShares(n);
    setShares(clamped);
    setSharesDraft(null);
    return clamped;
  };

  const bumpShares = (delta) => {
    setSharesDraft(null);
    setShares((s) => clampShares(s + delta));
  };

  const inputValue = sharesDraft !== null ? sharesDraft : String(shares);

  return (
    <div className="modal-overlay active" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal modal--invest" onClick={(e) => e.stopPropagation()}>
        <div className="invest-modal__hero">
          {heroImage ? (
            <img className="invest-modal__hero-media" src={heroImage} alt="" />
          ) : (
            <div className="invest-modal__hero-placeholder" aria-hidden="true">
              <i className="fas fa-home"></i>
            </div>
          )}
          <div className="invest-modal__hero-text">
            <span className="invest-modal__eyebrow"><i className="fas fa-chart-line"></i> Invest</span>
            <h2>{property.title}</h2>
            <p className="invest-modal__location">
              <span>{displayCountry.flag}</span>
              <span>{property.location}</span>
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="invest-modal__scroll">
          {ownedSharesMessage ? (
            <div className="ownership-notice ownership-notice--modal" role="status">
              <i className="fas fa-check-circle" aria-hidden="true"></i>
              <span>{ownedSharesMessage}</span>
            </div>
          ) : null}
          <div className="invest-modal__stats">
            <div className="invest-modal__stat">
              <label>Per share</label>
              <span className="invest-modal__stat-value">{formatUsd(pricePerShare)}</span>
            </div>
            <div className="invest-modal__stat">
              <label>Available</label>
              <span className="invest-modal__stat-value">{available.toLocaleString()}</span>
            </div>
            <div className="invest-modal__stat invest-modal__stat--highlight">
              <label>Your pick</label>
              <span className="invest-modal__stat-value">{draftSharesValue} share{draftSharesValue !== 1 ? "s" : ""}</span>
            </div>
          </div>

          <div className="invest-modal__shares-panel">
            <label className="invest-modal__shares-label" htmlFor="invest-shares-input">
              Number of shares
            </label>
            <div className="invest-modal__shares-control">
              <button
                type="button"
                className="invest-modal__shares-adjust"
                disabled={!canInvest || draftSharesValue <= 1}
                onClick={() => bumpShares(-1)}
                aria-label="Decrease shares"
              >
                <i className="fas fa-minus" aria-hidden="true"></i>
              </button>
              <input
                id="invest-shares-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="invest-modal__shares-input"
                value={inputValue}
                disabled={!canInvest}
                aria-label="Number of shares"
                aria-valuemin={1}
                aria-valuemax={maxShares}
                aria-valuenow={draftSharesValue}
                onFocus={(e) => {
                  setSharesDraft(String(shares));
                  e.target.select();
                }}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "");
                  setSharesDraft(digits);
                }}
                onBlur={() => commitSharesInput(sharesDraft)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitSharesInput(sharesDraft);
                    e.currentTarget.blur();
                  }
                }}
              />
              <button
                type="button"
                className="invest-modal__shares-adjust"
                disabled={!canInvest || draftSharesValue >= maxShares}
                onClick={() => bumpShares(1)}
                aria-label="Increase shares"
              >
                <i className="fas fa-plus" aria-hidden="true"></i>
              </button>
            </div>
            <p className="invest-modal__shares-hint">
              Type a number or use +/− · max {maxShares.toLocaleString()}
              {ownershipPct >= 0.01 ? ` · ~${ownershipPct < 1 ? ownershipPct.toFixed(2) : ownershipPct.toFixed(1)}% ownership` : ""}
            </p>
          </div>

          <SaleInvestmentCostBreakdown
            estimate={{
              subtotalUsd,
              acquisitionFeeUsd,
              vatAmountUsd,
              totalUsd,
              vatLabel,
              vatRate: quote?.vatRate ?? taxMeta.rate,
            }}
            country={country}
            compact
            title="Cost breakdown"
          />

          {tokenizationEnabled ? (
            <div className="invest-modal__payment-rail">
              <p className="invest-modal__payment-rail-label">Payment method</p>
              <div className="invest-modal__payment-rail-options" role="radiogroup" aria-label="Payment method">
                <button
                  type="button"
                  role="radio"
                  aria-checked={settlementRail === "fiat"}
                  className={`invest-modal__payment-rail-option${settlementRail === "fiat" ? " invest-modal__payment-rail-option--active" : ""}`}
                  onClick={() => setSettlementRail("fiat")}
                >
                  <i className="fas fa-wallet" aria-hidden="true"></i>
                  <span>
                    <strong>USD wallet</strong>
                    <small>Debit your CoOwn balance (card-funded)</small>
                  </span>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={settlementRail === "algorand"}
                  className={`invest-modal__payment-rail-option${settlementRail === "algorand" ? " invest-modal__payment-rail-option--active" : ""}`}
                  onClick={() => setSettlementRail("algorand")}
                  disabled={!cryptoCheckoutReady}
                >
                  <i className="fab fa-algolia" aria-hidden="true"></i>
                  <span>
                    <strong>Algorand</strong>
                    <small>
                      {cryptoCheckoutReady
                        ? "Pay with USDC or ALGO on Algorand"
                        : "Crypto checkout not configured yet"}
                    </small>
                  </span>
                </button>
              </div>
              {settlementRail === "algorand" && cryptoCheckoutReady ? (
                <div className="invest-modal__pay-asset">
                  <span className="invest-modal__pay-asset-label">Pay with</span>
                  <div className="invest-modal__pay-asset-options">
                    <button
                      type="button"
                      className={`invest-modal__pay-asset-btn${payAsset === "USDCa" ? " invest-modal__pay-asset-btn--active" : ""}`}
                      onClick={() => setPayAsset("USDCa")}
                    >
                      USDC
                    </button>
                    <button
                      type="button"
                      className={`invest-modal__pay-asset-btn${payAsset === "ALGO" ? " invest-modal__pay-asset-btn--active" : ""}`}
                      onClick={() => setPayAsset("ALGO")}
                      disabled={!algoSwapReady}
                    >
                      ALGO
                    </button>
                  </div>
                  <p className="invest-modal__pay-asset-note">
                    {payAsset === "ALGO"
                      ? algoSettlementMode === "testnet-algo-direct"
                        ? `Testnet: pay ALGO directly (${testnetAlgoUsdRate || 1} ALGO ≈ $1 USD). Production uses Tinyman ALGO→USDC swap.`
                        : algoSwapReady
                          ? "ALGO is converted to USDC at a live rate before your shares are issued."
                          : "ALGO swap unavailable on this network. Use USDC."
                      : "USDC on Algorand. Matches total USD due."}
                  </p>
                  {algoWallet?.address ? (
                    <div className="invest-modal__crypto-balances" role="status">
                      <span>
                        Your wallet:{" "}
                        <strong>{Number(algoWallet.algoBalance || 0).toLocaleString(undefined, { maximumFractionDigits: 6 })} ALGO</strong>
                        {cryptoCheckoutReady ? (
                          <>
                            {" · "}
                            <strong>{Number(algoWallet.usdcBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDC</strong>
                          </>
                        ) : null}
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="invest-modal__receipt">
            <div className="invest-modal__receipt-body">
              <InvestReceiptLine title="Shares" value={`${draftSharesValue} × ${formatUsd(pricePerShare)}`} />
              <InvestReceiptLine title="Subtotal" value={formatUsd(subtotalUsd)} />
              <InvestReceiptLine
                title="Acquisition fee (5%)"
                hint="Sourcing, legal structure, and due diligence"
                value={formatUsd(acquisitionFeeUsd)}
              />
              <InvestReceiptLine
                title={vatLabel}
                hint={vatNote || undefined}
                value={vatAmountUsd > 0 ? formatUsd(vatAmountUsd) : "$0"}
                valueMuted={vatAmountUsd <= 0}
              />
            </div>
          </div>

          <p className="invest-modal__fx-note">
            Charged in <strong>USD</strong>. Local currency ({getDisplayCountry(country).currency}) is indicative from live FX.
          </p>

          <p className="invest-modal__footnote">
            <i className="fas fa-info-circle" aria-hidden="true"></i>
            <span>Management fee 1% per year, billed quarterly (0.25%) from wallet, rental payouts, or card on file.</span>
          </p>
        </div>

        <div className="invest-modal__footer-sticky">
          <div className="invest-modal__footer-total">
            <div>
              <span className="invest-modal__footer-total-label">Total charged</span>
              <span className="invest-modal__footer-total-local">≈ {formatExactMoney(totalUsd, country)}</span>
            </div>
            <strong className="invest-modal__footer-total-amount">{formatUsd(totalUsd)}</strong>
          </div>
          <div className={`invest-modal__balance${!usesCryptoCheckout && balanceAfter < 0 ? " invest-modal__balance--low" : ""}`}>
            <span>{usesCryptoCheckout ? "CoOwn USD wallet (not used)" : "Wallet balance"}</span>
            <strong>{formatUsd(balance)}</strong>
          </div>
          {usesCryptoCheckout && paying ? (
            <p className="invest-modal__processing-note" role="status">
              <i className="fas fa-hourglass-half" aria-hidden="true"></i>
              Stay on this page until checkout completes. Closing the tab may interrupt settlement.
            </p>
          ) : null}
          {usesCryptoCheckout && cryptoFundsWarning ? (
            <p className="invest-modal__balance-warning">{cryptoFundsWarning}</p>
          ) : null}
          {!canInvest ? (
            <p className="invest-modal__balance-warning">
              {usesCryptoCheckout
                ? "No shares available at this amount."
                : "Insufficient balance for this property (including fees and tax)."}
            </p>
          ) : null}
          <button
            type="button"
            className="btn btn-primary invest-modal__cta"
            disabled={!canInvest || paying || cryptoCheckoutBlocked}
            onClick={async () => {
              const n = commitSharesInput(sharesDraft);
              setPaying(true);
              try {
                await onConfirm(n, totalUsd, settlementRail, payAsset);
              } finally {
                setPaying(false);
              }
            }}
          >
            <i className={`fas fa-${paying ? "spinner fa-spin" : "lock"}`} aria-hidden="true"></i>
            {usesCryptoCheckout
              ? paying
                ? "Processing crypto payment…"
                : `Pay with ${payAsset} & invest`
              : "Confirm investment"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvestModal;
